import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useOperator } from '../contexts/OperatorContext'

//
// Butler Amusements × MagicMoney login card.
//
// Visual language blends Butler's marquee colors (midway blue + hot magenta
// + marquee gold) with MagicMoney admin-portal structure (44566C ink,
// E9EBF2 hairline borders, 4 px corners, signature offset-left card shadow,
// Work Sans / Montserrat type pairing). The classic Butler carousel-horse
// logo anchors the sign-in moment so operators know instantly this portal
// belongs to them.
//
export function LoginCard() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const operator = useOperator()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const result = await login(username, password)
    setLoading(false)
    if (result.ok) return
    // Fork 14 (2026-04-17): SMS MFA users now finish on the dedicated
    // verify page instead of seeing the graceful-block error. TOTP users
    // keep the legacy-login message since Fork 11 disabled enrollment,
    // so any TOTP response means the account is pinned to the legacy
    // surface.
    if (result.mfaRequired && result.mfaChannel === 'sms' && result.mfaPendingToken) {
      navigate('/auth/verify-sms', { replace: true })
      return
    }
    setError(result.error ?? 'Login failed')
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-butler-lobby dark:bg-slate-900 px-4 py-10 overflow-hidden">
      {/* Decorative backdrop — layered soft marquee glow so the blue wash
          breathes without stealing focus from the card. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-70 dark:opacity-40"
        style={{
          background:
            `radial-gradient(1200px 600px at 20% 10%, var(--brand-radial-1), transparent 55%), radial-gradient(900px 500px at 85% 90%, var(--brand-radial-2), transparent 60%), radial-gradient(800px 500px at 50% 110%, var(--brand-radial-3), transparent 60%)`,
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Brand lockup */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="mm-card flex items-center justify-center p-2 mb-5 shadow-card-soft">
            <img
              src={operator.logoUrl}
              alt={`${operator.company} logo`}
              className="h-20 w-auto"
            />
          </div>
          <h1 className="font-display text-3xl font-bold text-butler-700 dark:text-butler-100 tracking-tight">
            {operator.short}
          </h1>
          <p className="mt-1 text-sm text-ink-soft dark:text-slate-300">
            {operator.company} operator portal
          </p>
          <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-butler-600 dark:text-butler-200 font-semibold">
            <span className="h-px w-6 bg-hairline" />
            <span>Sign in to continue</span>
            <span className="h-px w-6 bg-hairline" />
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mm-card rounded-panel p-6 space-y-4 border-t-4 border-butler-600 dark:bg-slate-800 dark:border-butler-400"
        >
          <div>
            <label
              htmlFor="username"
              className="block text-xs font-semibold uppercase tracking-[0.12em] text-ink-soft dark:text-slate-300 mb-2"
            >
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-card border border-hairline-soft dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-ink dark:text-white placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-butler-500 focus:border-butler-500 transition-colors"
              placeholder="sean"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-xs font-semibold uppercase tracking-[0.12em] text-ink-soft dark:text-slate-300 mb-2"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-card border border-hairline-soft dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2.5 text-ink dark:text-white placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-butler-500 focus:border-butler-500 transition-colors"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="text-sm text-marquee-700 bg-marquee-50 dark:bg-marquee-900/20 dark:text-marquee-100 rounded-card border border-marquee-200 dark:border-marquee-700 px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-card bg-butler-600 hover:bg-butler-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-butler-500 text-white font-semibold uppercase tracking-[0.16em] py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_20px_rgba(90,120,255,.25)]"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <a
            href="/forgot-password"
            className="text-sm text-ink-soft dark:text-slate-400 hover:text-butler-600 dark:hover:text-butler-400 transition-colors"
          >
            Forgot password?
          </a>
        </div>

        <p className="mt-4 text-center text-xs text-ink-soft dark:text-slate-400">
          <span className="marquee-dot mr-2 align-middle" />
          Built for {operator.company} — powered by MagicMoney.
        </p>
      </div>
    </div>
  )
}
